import {
  AlertIcon, CheckCircleFillIcon, CircleSlashIcon, ClockIcon, DotFillIcon, EyeIcon, GitMergeIcon, GitPullRequestClosedIcon,
  GitPullRequestIcon, HistoryIcon, SkipIcon, StopIcon, SyncIcon, VerifiedIcon, XCircleFillIcon,
} from "@primer/octicons-react";
import type { ChangeState, Evidence } from "../api/types";
import { cn } from "../lib/utils";
import { Badge, toneText, type Tone } from "./ui/badge";

const STATES: Record<ChangeState, { tone: Tone; label: string; Icon: typeof GitPullRequestIcon }> = {
  created: { tone: "green", label: "Open", Icon: GitPullRequestIcon },
  ci: { tone: "orange", label: "CI", Icon: SyncIcon },
  review: { tone: "yellow", label: "Needs review", Icon: EyeIcon },
  ready: { tone: "blue", label: "Ready", Icon: ClockIcon },
  train: { tone: "blue", label: "In train", Icon: SyncIcon },
  landing: { tone: "blue", label: "Landing", Icon: SyncIcon },
  landed: { tone: "purple", label: "Landed", Icon: GitMergeIcon },
  verified: { tone: "purple", label: "Verified", Icon: VerifiedIcon },
  reverted: { tone: "default", label: "Reverted", Icon: HistoryIcon },
  rejected: { tone: "red", label: "Rejected", Icon: GitPullRequestClosedIcon },
  conflict: { tone: "yellow", label: "Conflict", Icon: AlertIcon },
  cancelled: { tone: "default", label: "Cancelled", Icon: CircleSlashIcon },
};

export function stateInfo(s: ChangeState) {
  return STATES[s] ?? { tone: "default" as Tone, label: s, Icon: DotFillIcon };
}

export function StateLabel({ state, big }: { state: ChangeState; big?: boolean }) {
  const { tone, label, Icon } = stateInfo(state);
  return (
    <Badge tone={tone} variant={big ? "state" : "outline"}>
      <Icon size={big ? 14 : 12} className={Icon === SyncIcon ? "animate-spin" : undefined} /> {label}
    </Badge>
  );
}

export function StateIcon({ state }: { state: ChangeState }) {
  const { tone, Icon } = stateInfo(state);
  return (
    <span className={cn("mt-[3px] inline-flex", toneText[tone])}>
      <Icon size={16} className={Icon === SyncIcon ? "animate-spin" : undefined} />
    </span>
  );
}

export function EvidenceIcon({ status }: { status: Evidence["status"] }) {
  switch (status) {
    case "pass":
      return <CheckCircleFillIcon className="text-success" />;
    case "fail":
      return <XCircleFillIcon className="text-danger" />;
    case "infra_error":
      return <StopIcon className="text-warning" />;
    case "skipped":
      return <SkipIcon className="text-muted-foreground" />;
    case "waiting":
      return <ClockIcon className="text-muted-foreground" aria-label="waiting for needs" />;
    default:
      return <SyncIcon className="animate-spin text-warning" />;
  }
}

export function TierLabel({ tier }: { tier: string }) {
  return <Badge tone={tier === "high" ? "red" : tier === "low" ? "default" : "blue"}>{tier} risk</Badge>;
}
